/**
 * request 网络请求工具
 * 更详细的 api 文档: https://github.com/umijs/umi-request
 */
import { extend } from 'umi-request';
import { message, notification } from 'antd';
import { setUser, getUser } from './authority';
import { removeBlankProperty } from '@/utils/utils';
import alidnsError from './alidnsError';
import { history } from 'umi';
import { reportAPI } from '@/aliLogger';
import { getToken, clearToken, setToken } from '@/utils/token';
import { getVisitorId } from '@/utils/fingerprint';

// API 路径常量
const API_PATHS = {
  AUTHORIZATIONS: '/api/authorizations',
  AUTHORIZATIONS_REFRESH: '/api/authorizations/refresh',
  NOTIFICATIONS: '/api/notifications',
};

const codeMessage = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  429: '请求过于频繁，请稍后再试。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

// 用于跟踪当前显示的 notification，避免重复显示相同的错误信息
const activeNotificationKeys = new Set();

/**
 * 生成 notification 的唯一 key
 */
const generateNotificationKey = (message, description) => {
  return `notification_${message}_${description || ''}`;
};

/**
 * 显示去重后的 notification
 * 如果相同的错误信息已经存在，则不再显示
 */
const showNotificationOnce = config => {
  const { message, description } = config;
  const key = generateNotificationKey(message, description || '');

  // 如果该 notification 已经存在，则不重复显示
  if (activeNotificationKeys.has(key)) {
    return;
  }

  // 添加到活跃集合
  activeNotificationKeys.add(key);

  // 显示 notification，并在关闭时从集合中移除
  notification.error({
    ...config,
    key,
    onClose: () => {
      activeNotificationKeys.delete(key);
      if (config.onClose) {
        config.onClose();
      }
    },
  });
};
/**
 * 异常处理程序
 */
const errorHandler = async error => {
  const { response, data } = error;
  // 记录并准备手动上报（仅错误会进入此处）
  const begin = error?.request?.options?.__report_begin || Date.now();
  const url = error?.request?.originUrl || error?.request?.url || '';
  const reportNow = async () => {
    try {
      const code = data?.status_code ?? response?.status ?? -1;
      const messageText = data.message ?? '';
      const time = Date.now() - begin;
      reportAPI({ url, success: 0, time, code, message: messageText, begin });
    } catch (e) {
      console.log(e);
    }
  };
  reportNow();
  let body = '';
  if (response && response.status === 504) {
    showNotificationOnce({
      message: '请求错误',
      description: '请求超时，稍后再试',
    });
    return response;
  }
  if (response && response.json) {
    try {
      body = await response.clone().json();
    } catch (e) {
      if (response.status < 400) return response;
    }
  }

  if (body && body.status_code === 500 && body.message === 'This action is unauthorized.') {
    message.error('没有操作权限');
    return response;
  }
  if (body && body.status_code) {
    if (body.status_code === 401) {
      const token = getToken();

      // 登录接口401直接清空
      if (window.location.pathname === '/user/login') {
        setUser({});
        clearToken();
      }
      // 刷新接口401，清空并跳转登录
      else if (error?.request?.originUrl === API_PATHS.AUTHORIZATIONS_REFRESH) {
        redirectToLogin();
        return response;
      }
      // 其他接口401，尝试刷新token
      else {
        // 检查是否有 token 和用户信息，如果都没有说明从未登录，直接跳转
        const currentUser = getUser();
        if (!token || !token.access_token || !currentUser || !currentUser.id) {
          redirectToLogin();
          return response;
        }

        try {
          await refreshAccessToken();

          // 刷新成功，重试原请求
          const newToken = getToken();
          const retryOptions = {
            ...error.request.options,
            headers: {
              ...error.request.options.headers,
              Authorization: `Bearer ${newToken.access_token}`,
            },
          };
          return request(error.request.url, retryOptions);
        } catch (refreshError) {
          // 刷新失败，清空并跳转登录
          redirectToLogin();
          return response;
        }
      }
    }
    // refresh 接口的 429 错误特殊处理：抛出错误，不清空 token，不跳转登录
    if (
      body.status_code === 429 &&
      (error?.request?.originUrl === API_PATHS.AUTHORIZATIONS_REFRESH ||
        url === API_PATHS.AUTHORIZATIONS_REFRESH)
    ) {
      const retryAfter = parseInt(response?.headers?.get('Retry-After') || '2', 10) * 1000;
      console.warn('Refresh rate limited, retry after:', retryAfter);
      return response;
    }
    const errorText = body.message || codeMessage[body.status_code];
    if (body.errors) {
      const { errors } = body;
      Object.keys(errors).forEach(key => {
        if (Array.isArray(errors[key])) {
          errors[key].forEach(str => {
            message.error(str);
          });
        } else {
          message.error(errors[key]);
        }
      });
    } else {
      message.error(alidnsError.find(i => i.ErrorCode === errorText)?.Description || errorText);
    }
  } else if (response && response.status) {
    const { status, statusText = '' } = response;
    // refresh 接口的 429 错误特殊处理（HTTP 状态码，非 body.status_code）
    if (
      status === 429 &&
      (error?.request?.originUrl === API_PATHS.AUTHORIZATIONS_REFRESH ||
        url === API_PATHS.AUTHORIZATIONS_REFRESH)
    ) {
      const retryAfter = parseInt(response.headers?.get('Retry-After') || '2', 10) * 1000;
      console.warn('Refresh rate limited, retry after:', retryAfter);
      return response;
    }
    const errorText = codeMessage[status] || statusText;
    showNotificationOnce({
      message: '请求错误',
      description: errorText,
    });
  } else if (error && error.status) {
    const errorText = error.statusText || codeMessage[error.status];
    showNotificationOnce({
      message: '请求错误',
      description: errorText,
    });
  } else if (!response) {
    showNotificationOnce({
      description: '您的网络发生异常，无法连接服务器',
      message: '网络异常',
    });
  }
  return response;
};

/**
 * 配置request请求时的默认参数
 */
const REFRESH_LEEWAY = 10 * 1000; // 提前10秒刷新
let refreshPromise = null;

// 导出函数，用于检查 refresh 是否正在进行
export function isRefreshing() {
  return refreshPromise !== null;
}

// 统一处理登录跳转
const redirectToLogin = () => {
  message.error('登录状态过期');
  clearToken();
  setUser({});
  window.location.replace(`${window.location.origin}/user/login`);
};

const request = extend({
  // 默认错误`处理
  errorHandler,
  credentials: 'include', // 默认请求是否带上cookie
});

const isAuthPath = (url, method) => {
  const upperMethod = (method || '').toUpperCase();
  // 登录（POST /api/authorizations）和刷新接口不需要刷新 token
  // 退出登录（DELETE /api/authorizations）需要 token，且可能需要刷新
  const isLoginPage = ['/user/login', '/user-authorize', '/oauth-authorize'].includes(
    window.location.pathname,
  );

  return (
    (url === API_PATHS.AUTHORIZATIONS && upperMethod === 'POST') ||
    url === API_PATHS.AUTHORIZATIONS_REFRESH ||
    isLoginPage
  );
};

// expires_in 是"距离过期还剩多少秒"，这里统一转换为毫秒进行比较
// 如果 token 已经过期，或者即将在 REFRESH_LEEWAY 时间内过期，返回 true
export const shouldRefreshSoon = token => {
  if (!token?.access_token) return false;

  const expiresInMs = Number(token.expires_in) * 1000;
  const createdAt = Number(token.token_created_at) || 0;

  // 如果 expires_in 或 token_created_at 无效，无法判断是否过期，返回 false
  // 这种情况下，如果 token 真的过期了，服务器会返回 401，由错误处理器处理
  if (!Number.isFinite(expiresInMs) || expiresInMs <= 0 || !createdAt) {
    return false;
  }

  const expiresAt = createdAt + expiresInMs;
  const timeUntilExpiry = expiresAt - Date.now();

  // 如果已经过期，或者即将在 REFRESH_LEEWAY 时间内过期，返回 true
  return timeUntilExpiry <= REFRESH_LEEWAY;
};

export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  const doRefresh = async () => {
    // 获取 visitorId
    const visitorId = await getVisitorId().catch(() => 'unknown');

    const res = await fetch(API_PATHS.AUTHORIZATIONS_REFRESH, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-Visitor-Id': visitorId,
      },
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('refresh_token_expired');
      }
      throw new Error(`refresh failed: ${res.status}`);
    }

    const data = await res.json();
    if (data?.access_token) {
      const tokenPayload = {
        access_token: data.access_token,
        token_type: data.token_type || 'Bearer',
        expires_in: data.expires_in || 0,
        token_created_at: Date.now(),
      };
      setToken(tokenPayload);
      return tokenPayload;
    }
    throw new Error('refresh no token');
  };

  refreshPromise = doRefresh()
    .catch(err => {
      redirectToLogin();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * 配置request拦截器, 改变url 或 options.
 */
request.interceptors.request.use(async (url, options) => {
  let token = getToken();

  // 如果已有刷新在进行，等待刷新完成
  if (!isAuthPath(url, options?.method) && refreshPromise) {
    try {
      await refreshPromise;
      token = getToken();
    } catch (e) {
      // 刷新失败，使用当前 token（可能还未过期）
      token = getToken();
    }
  }

  // 检查 token 是否需要刷新（即将过期或已过期）
  const needRefresh = !isAuthPath(url, options?.method) && shouldRefreshSoon(token);

  if (needRefresh) {
    try {
      await refreshAccessToken();
      token = getToken();
    } catch (e) {
      // 刷新失败，清空并跳转登录
      redirectToLogin();
      return Promise.reject(e);
    }
  }

  // 获取 visitorId（异步，但不阻塞请求）
  const visitorIdPromise = getVisitorId().catch(() => 'unknown');

  // 设置请求头
  const optionsData =
    options?.data && options?.removeBlankProperty
      ? { data: removeBlankProperty(options?.data) }
      : {};
  const defaultHeaders = {
    Accept: 'application/json',
    ...options?.headers,
    params: options?.removeBlankProperty ? removeBlankProperty(options?.params) : options?.params,
    ...optionsData,
  };

  if (token && token.access_token && options.headers?.Authorization !== 'ignore') {
    defaultHeaders.Authorization = `Bearer ${token.access_token}`;
  }

  // 等待 visitorId 并添加到请求头
  const visitorId = await visitorIdPromise;
  defaultHeaders['X-Visitor-Id'] = visitorId;

  const newOptions = { ...options, headers: defaultHeaders };

  if (
    newOptions.method === 'POST' ||
    newOptions.method === 'PUT' ||
    newOptions.method === 'DELETE'
  ) {
    if (!(newOptions.body instanceof FormData)) {
      newOptions.headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8',
        ...newOptions.headers,
      };
      newOptions.body = JSON.stringify(newOptions.body);
    } else {
      // newOptions.body is FormData
      newOptions.headers = {
        Accept: 'application/json',
        ...newOptions.headers,
      };
    }
  }

  // 记录请求开始时间，用于错误上报耗时
  newOptions.__report_begin = Date.now();

  return {
    url,
    options: { ...newOptions },
  };
});

request.interceptors.response.use(async response => {
  if (response.headers.get('x-alicdn-da-ups-status') == 'endOs,80,502') {
    showNotificationOnce({
      message: 'CDN加速异常',
      description: '建议使用in开头域名访问',
    });
  }
  if (
    response.headers.get('force_modify_password') == '1' &&
    !response.url.includes(API_PATHS.NOTIFICATIONS)
  ) {
    history.push({
      pathname: '/personal/info',
      state: {
        update: true,
      },
    });
  }
  if (response.headers.get('jump_to_modify_password') == '1') {
    history.push({
      pathname: '/personal/info',
      state: {
        update: true,
        jump: true,
      },
    });
  }

  return response;
});
export default request;
